import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';

import {
  FormBuilder,
  Validators
} from '@angular/forms';

import {
  ActivatedRoute,
  Router
} from '@angular/router';

import { finalize } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/services/auth.service';
import { ApiResult } from '../../core/models/api/api-result.model';
import { JwtTokenResultModel } from '../../core/models/AuthModels/jwt-token-result.model';


declare const grecaptcha: {

  render(
    container: HTMLElement,
    parameters: {
      sitekey: string;
      callback?: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
    }
  ): number;

  reset(widgetId?: number): void;
};


@Component({
  selector: 'app-login',
  standalone: false,
  styleUrl: './login.component.scss',
  templateUrl: './login.component.html'
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('recaptchaContainer')
  private readonly recaptchaContainer?: ElementRef<HTMLElement>;


  public readonly loginForm;

  public isSubmitting: boolean = false;

  public errorTitle: string = '';
  public errorMessage: string = '';

  public ShowPassword: boolean = false;


  public readonly recaptchaSiteKey: string =
    environment.googleRecaptchaSiteKey?.trim() ?? '';


  public readonly isRecaptchaEnabled: boolean =
    this.recaptchaSiteKey.length > 0;


  private recaptchaWidgetId: number | null = null;

  private recaptchaToken: string = '';


  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {

    this.loginForm = this.formBuilder.nonNullable.group({

      email: [
        '',
        [
          Validators.required,
          Validators.email
        ]
      ],

      password: [
        '',
        [
          Validators.required
        ]
      ]

    });


    // ============================================================
    // TEST LOGIN
    // PROD'A GEÇERKEN BU BLOĞU KALDIR
    // ============================================================

    this.loginForm.patchValue({
      email: 'p2@anameta.com',
      password: 'testtest'
    });


    this.loginForm.markAsPristine();
    this.loginForm.markAsUntouched();
  }


  ngOnInit(): void {

    this.redirectIfAuthenticated();
  }


  ngAfterViewInit(): void {

    // Kullanıcı zaten login olmuşsa reCAPTCHA yüklemeye gerek yok.
    if (
      this.authService.isLoggedIn() &&
      this.authService.isAccessTokenValid()
    ) {
      return;
    }


    if (!this.isRecaptchaEnabled) {
      return;
    }


    this.loadRecaptcha();
  }


  ngOnDestroy(): void {

    this.recaptchaWidgetId = null;
    this.recaptchaToken = '';
  }


  private redirectIfAuthenticated(): void {

    if (!this.authService.isLoggedIn()) {
      return;
    }


    if (!this.authService.isAccessTokenValid()) {

      // Token var ama geçersiz / süresi dolmuş.
      this.authService.logout();

      return;
    }


    const returnUrl =
      this.getReturnUrl();


    this.router.navigateByUrl(
      returnUrl
    );
  }


  public Login(): void {

    if (this.isSubmitting) {
      return;
    }


    this.clearError();


    if (this.loginForm.invalid) {

      this.loginForm.markAllAsTouched();

      this.showError(
        'Eksik Bilgi',
        'Lütfen e-posta adresinizi ve şifrenizi kontrol edin.'
      );

      return;
    }


    // if (
    //   this.isRecaptchaEnabled &&
    //   !this.recaptchaToken
    // ) {

    //   this.showError(
    //     'Doğrulama Gerekli',
    //     'Lütfen Google reCAPTCHA doğrulamasını tamamlayın.'
    //   );

    //   return;
    // }


    this.isSubmitting = true;


    const formValue =
      this.loginForm.getRawValue();


    this.authService.Login(formValue.email.trim(), formValue.password)
      .pipe(

        finalize(() => {

          this.isSubmitting = false;

          this.changeDetectorRef.detectChanges();
        })

      )
      .subscribe({

        next: (response: ApiResult<JwtTokenResultModel>) => {

          if (!response) {

            this.showError(
              'Giriş Başarısız',
              'Sunucudan yanıt alınamadı.'
            );

            this.resetRecaptcha();
            this.changeDetectorRef.detectChanges();
            return;
          }


          if (response.isError === true) {

            this.showError(
              'Giriş Başarısız',
              response.msg ||
              'E-posta adresi veya şifre hatalı.'
            );

            this.resetRecaptcha();
            this.changeDetectorRef.detectChanges();
            return;
          }


          if (!response.data) {

            this.showError(
              'Giriş Başarısız',
              'Sunucudan geçerli bir giriş yanıtı alınamadı.'
            );

            this.resetRecaptcha();
             this.changeDetectorRef.detectChanges();
            return;
          }


          const returnUrl =
            this.getReturnUrl();


          this.router.navigateByUrl(
            returnUrl
          );
        },


        error: (error: any) => {

          let message =
            'Sunucuya bağlanılamadı. Lütfen tekrar deneyin.';


          if (error?.error?.msg) {

            message =
              error.error.msg;
          }

          else if (error?.error?.message) {

            message =
              error.error.message;
          }

          else if (error?.message) {

            message =
              error.message;
          }


          this.showError(
            'Bağlantı Hatası',
            message
          );


          this.resetRecaptcha();
          this.changeDetectorRef.detectChanges();
        }

      });
  }


  public TogglePassword(): void {

    this.ShowPassword =
      !this.ShowPassword;
  }


  public clearError(): void {

    this.errorTitle = '';
    this.errorMessage = '';
  }


  private showError(title: string, message: string): void {

    this.isSubmitting = false;

    this.errorTitle = title;
    this.errorMessage = message;

    this.changeDetectorRef.detectChanges();
  }


  private getReturnUrl(): string {

    const returnUrl =
      this.activatedRoute.snapshot
        .queryParamMap
        .get('returnUrl');


    if (
      !returnUrl ||
      !returnUrl.startsWith('/') ||
      returnUrl.startsWith('//')
    ) {

      return '/dashboard';
    }


    return returnUrl;
  }


  private loadRecaptcha(): void {

    if (!this.isRecaptchaEnabled) {
      return;
    }


    const scriptId =
      'google-recaptcha-v2-script';


    const renderWidget = (): void => {

      if (
        !this.recaptchaContainer?.nativeElement ||
        this.recaptchaWidgetId !== null
      ) {

        return;
      }


      if (
        typeof grecaptcha === 'undefined' ||
        typeof grecaptcha.render !== 'function'
      ) {

        this.showError(
          'reCAPTCHA Hatası',
          'Google reCAPTCHA yüklenemedi. Lütfen sayfayı yenileyin.'
        );

        return;
      }


      this.recaptchaWidgetId =
        grecaptcha.render(
          this.recaptchaContainer.nativeElement,
          {

            sitekey:
              this.recaptchaSiteKey,


            callback: (token: string) => {

              this.recaptchaToken =
                token;


              if (
                this.errorTitle === 'Doğrulama Gerekli' ||
                this.errorTitle === 'reCAPTCHA Hatası'
              ) {

                this.clearError();
              }


              this.changeDetectorRef.detectChanges();
            },


            'expired-callback': () => {

              this.recaptchaToken = '';

              this.changeDetectorRef.detectChanges();
            },


            'error-callback': () => {

              this.recaptchaToken = '';


              this.showError(
                'reCAPTCHA Hatası',
                'reCAPTCHA doğrulaması tamamlanamadı. Lütfen tekrar deneyin.'
              );
            }

          }
        );
    };


    if (
      typeof grecaptcha !== 'undefined' &&
      typeof grecaptcha.render === 'function'
    ) {

      renderWidget();

      return;
    }


    const existingScript =
      document.getElementById(scriptId);


    if (existingScript) {

      existingScript.addEventListener(
        'load',
        () => {

          window.setTimeout(
            renderWidget,
            0
          );
        },
        {
          once: true
        }
      );


      return;
    }


    const script =
      document.createElement('script');


    script.id =
      scriptId;


    script.src =
      'https://www.google.com/recaptcha/api.js?render=explicit';


    script.async = true;
    script.defer = true;


    script.addEventListener(
      'load',
      () => {

        window.setTimeout(
          renderWidget,
          0
        );
      },
      {
        once: true
      }
    );


    script.addEventListener(
      'error',
      () => {

        this.showError(
          'reCAPTCHA Hatası',
          'Google reCAPTCHA dosyası yüklenemedi.'
        );
      },
      {
        once: true
      }
    );


    document.head.appendChild(script);
  }


  private resetRecaptcha(): void {

    this.recaptchaToken = '';


    if (
      !this.isRecaptchaEnabled ||
      this.recaptchaWidgetId === null ||
      typeof grecaptcha === 'undefined' ||
      typeof grecaptcha.reset !== 'function'
    ) {

      return;
    }


    grecaptcha.reset(
      this.recaptchaWidgetId
    );
  }
}